import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Tabs, Tab, Button, TextField,
  List, ListItem, ListItemText, ListItemSecondaryAction, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip, Stack,
  Alert, CircularProgress
} from '@mui/material';
import { Add, Delete, Edit, Group as GroupIcon, Person, AccountBalanceWallet } from '@mui/icons-material';
import { friendsApi, friendGroupsApi } from '../../services/api';
import { useNotification } from '../../hooks/useNotification';
import GroupDialog from './GroupDialog';
import GroupBalancesDialog from './GroupBalancesDialog';

function FriendsSection() {
  const [activeTab, setActiveTab] = useState(0);
  const [friends, setFriends] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [addFriendDialog, setAddFriendDialog] = useState(false);
  // null = closed, { group: null } = create, { group } = edit
  const [groupDialog, setGroupDialog] = useState(null);
  const [balancesGroupId, setBalancesGroupId] = useState(null);
  const [newFriendEmail, setNewFriendEmail] = useState('');
  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [friendsRes, groupsRes] = await Promise.all([
        friendsApi.getAll(),
        friendGroupsApi.getAll()
      ]);
      setFriends(friendsRes.data.friends || []);
      setMe(friendsRes.data.me || null);
      setGroups(groupsRes.data.groups || []);
    } catch (error) {
      console.error('Error loading data:', error);
      showError('Failed to load friends and groups');
    } finally {
      setLoading(false);
    }
  };

  // Refresh without the full-page spinner so the dialog closes smoothly
  const reloadQuietly = async () => {
    const [friendsRes, groupsRes] = await Promise.all([
      friendsApi.getAll(),
      friendGroupsApi.getAll()
    ]);
    setFriends(friendsRes.data.friends || []);
    setGroups(groupsRes.data.groups || []);
  };

  const handleGroupSaved = async (message) => {
    showSuccess(message);
    try {
      await reloadQuietly();
    } catch (error) {
      showError('Failed to refresh groups');
    }
  };

  const memberSummary = (group) => {
    const names = group.members
      .filter(m => m.email !== me?.email)
      .map(m => m.name || m.email);
    const shown = names.slice(0, 3).join(', ');
    return names.length > 3 ? `You, ${shown} +${names.length - 3} more` : ['You', shown].filter(Boolean).join(', ');
  };

  const handleAddFriend = async () => {
    try {
      await friendsApi.add(newFriendEmail);
      showSuccess('Friend added successfully');
      setNewFriendEmail('');
      setAddFriendDialog(false);
      await loadData();
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to add friend');
    }
  };

  const handleRemoveFriend = async (id) => {
    if (!window.confirm('Remove this friend?')) return;
    try {
      await friendsApi.remove(id);
      showSuccess('Friend removed');
      await loadData();
    } catch (error) {
      showError('Failed to remove friend');
    }
  };

  const handleDeleteGroup = async (id) => {
    if (!window.confirm('Delete this group?')) return;
    try {
      await friendGroupsApi.delete(id);
      showSuccess('Group deleted');
      await loadData();
    } catch (error) {
      showError('Failed to delete group');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Friends" icon={<Person />} iconPosition="start" />
          <Tab label="Groups" icon={<GroupIcon />} iconPosition="start" />
        </Tabs>

        <CardContent>
          {activeTab === 0 && (
            <Box>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography variant="h6">My Friends ({friends.length})</Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setAddFriendDialog(true)}
                >
                  Add Friend
                </Button>
              </Box>

              {friends.length === 0 ? (
                <Alert severity="info">No friends added yet. Add friends to split expenses!</Alert>
              ) : (
                <List>
                  {friends.map((friend) => (
                    <ListItem key={friend.id}>
                      <ListItemText
                        primary={friend.name || friend.email}
                        secondary={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <span>{friend.email}</span>
                            {friend.isOnPlatform ? (
                              <Chip label="On Platform" size="small" color="success" />
                            ) : (
                              <Chip label="Not on Platform" size="small" color="default" />
                            )}
                          </Stack>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" aria-label="Remove friend" onClick={() => handleRemoveFriend(friend.id)}>
                          <Delete />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography variant="h6">My Groups ({groups.length})</Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setGroupDialog({ group: null })}
                >
                  Create Group
                </Button>
              </Box>

              {groups.length === 0 ? (
                <Alert severity="info">No groups created yet. Create groups to organize your expenses!</Alert>
              ) : (
                <List>
                  {groups.map((group) => (
                    <ListItem key={group.id}>
                      <ListItemText
                        primary={group.name}
                        secondary={`${group.memberCount} members: ${memberSummary(group)} · Created by ${group.isCreator ? 'you' : group.createdBy.name || group.createdBy.email || 'unknown'}`}
                        sx={{ pr: group.isCreator ? 15 : 10 }}
                      />
                      <ListItemSecondaryAction>
                        <IconButton aria-label="Group balances and settle up" onClick={() => setBalancesGroupId(group.id)}>
                          <AccountBalanceWallet />
                        </IconButton>
                        <IconButton aria-label="Edit group" onClick={() => setGroupDialog({ group })}>
                          <Edit />
                        </IconButton>
                        {group.isCreator && (
                          <IconButton edge="end" aria-label="Delete group" onClick={() => handleDeleteGroup(group.id)}>
                            <Delete />
                          </IconButton>
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add Friend Dialog */}
      <Dialog open={addFriendDialog} onClose={() => setAddFriendDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Friend</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Friend's Email"
            type="email"
            fullWidth
            value={newFriendEmail}
            onChange={(e) => setNewFriendEmail(e.target.value)}
            helperText="Add anyone by email - no invitation required!"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddFriendDialog(false)}>Cancel</Button>
          <Button onClick={handleAddFriend} variant="contained" disabled={!newFriendEmail}>
            Add Friend
          </Button>
        </DialogActions>
      </Dialog>

      <GroupDialog
        open={!!groupDialog}
        group={groupDialog?.group || null}
        friends={friends}
        me={me}
        onClose={() => setGroupDialog(null)}
        onSaved={handleGroupSaved}
      />

      <GroupBalancesDialog
        open={!!balancesGroupId}
        groupId={balancesGroupId}
        onClose={() => setBalancesGroupId(null)}
      />
    </Box>
  );
}

export default FriendsSection;

